import React, { useState } from "react";
import { CustomerHeader } from "../components/customers/customerDetail/CustomerHeader";
import { CustomerTabs } from "../components/customers/customerDetail/CustomerTabs";
import { GameRecordTable } from "../components/customers/customerDetail/GameRecordTable";
import { FacilitiesTable } from "../components/customers/customerDetail/FacilitiesTable";
import { CashInOutTable } from "../components/customers/customerDetail/CashInOutTable";
import { CustomerDetailsDrawer } from "../components/customers/customerDetail/CustomerDetailsDrawer";
import { useParams } from "react-router-dom";
import { useCustomer } from "../hooks/useCustomers";
import { Customer } from "../types/customer";

export const CustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const [activeTab, setActiveTab] = useState(0);

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");

  const [selectedGames, setSelectedGames] = useState<string[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    data: customerDetail,
    isLoading,
    isError,
    error,
  } = useCustomer(customerId || "");

  const customer = customerDetail?.customer;
  const financials = customerDetail?.financials;
  const lastEntry = customerDetail?.lastEntry;

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleDateRangeChange = (dateFrom: string, dateTo: string) => {
    setSelectedDateFrom(dateFrom);
    setSelectedDateTo(dateTo);
    setSelectedDate("");
  };

  const handleDateClear = () => {
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleGamesChange = (gameIds: string[]) => {
    setSelectedGames(gameIds);
  };

  const handleTablesChange = (tableIds: string[]) => {
    setSelectedTables(tableIds);
  };

  const handleDetailsClick = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleEditFromDrawer = (customer: Customer) => {
    setIsDrawerOpen(false);

    console.log("Edit customer:", customer);
  };

  const handleDeleteFromDrawer = (customerId: string) => {
    setIsDrawerOpen(false);

    console.log("Delete customer:", customerId);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <GameRecordTable
            customerId={customerId}
            selectedDate={selectedDate}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            selectedGames={selectedGames}
            selectedTables={selectedTables}
          />
        );
      case 1:
        return (
          <CashInOutTable
            customerId={customerId}
            selectedDate={selectedDate}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
          />
        );
      case 2:
        return <FacilitiesTable />;
      default:
        return (
          <GameRecordTable
            customerId={customerId}
            selectedDate={selectedDate}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            selectedGames={selectedGames}
            selectedTables={selectedTables}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-400">Loading customer details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>
              Error loading customer: {error?.message || "Customer not found"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        <CustomerHeader
          customer={customer}
          activeTab={activeTab}
          selectedDate={selectedDate}
          selectedDateFrom={selectedDateFrom}
          selectedDateTo={selectedDateTo}
          onDateChange={handleDateChange}
          onDateRangeChange={handleDateRangeChange}
          onDateClear={handleDateClear}
          selectedGames={selectedGames}
          onGamesChange={handleGamesChange}
          selectedTables={selectedTables}
          onTablesChange={handleTablesChange}
          onDetailsClick={handleDetailsClick}
          financials={financials}
        />
        <CustomerTabs activeTab={activeTab} onTabChange={setActiveTab} />
        {renderTabContent()}
      </div>

      {/* Customer Details Drawer */}
      <CustomerDetailsDrawer
        customer={customer}
        lastEntry={lastEntry?.date}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onEdit={handleEditFromDrawer}
        onDelete={handleDeleteFromDrawer}
      />
    </div>
  );
};
